import React, { useMemo, useState } from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Section, ErrorPanel, LoadingRow, EmptyNote, UserLink, TableWrap, Th, Td } from '../components/ui/bits';
import { useRpc, useSel, queryClient } from '../lib/queries';
import { supabase } from '../lib/supabase';
import { agoLabel, fmtDate, fmtDateTime, isoDaysAgo } from '../lib/format';

export const AlertsPage: React.FC = () => {
  const { data: cfgRows } = useSel<any[]>(['pipeline-config'], () =>
    supabase.from('pipeline_config').select('*').eq('id', 1));
  const cfg = cfgRows?.[0];
  const { data: events, error, isLoading } = useSel<any[]>(['open-events'], () =>
    supabase.from('firm_open_events').select('*').order('created_at', { ascending: false }).limit(60));
  const { data: sends } = useSel<any[]>(['alert-sends-recent'], () =>
    supabase.from('alert_sends').select('user_id, channel, sent_at, firm_open_event_id').gte('sent_at', isoDaysAgo(30)).order('sent_at', { ascending: false }).limit(200));
  const { data: deadBells } = useRpc<any[]>('admin_dead_bells');
  const { data: seedBlocked } = useRpc<any[]>('admin_seed_blocked_candidates');
  const { data: dupes } = useRpc<any[]>('admin_duplicate_firm_names');

  const [confirmKill, setConfirmKill] = useState(false);
  const [rowAction, setRowAction] = useState<{ kind: 'replay' | 'suppress'; ev: any } | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [emitResult, setEmitResult] = useState<any>(null);
  const [emitBusy, setEmitBusy] = useState<'dry' | 'live' | null>(null);
  const [confirmEmit, setConfirmEmit] = useState(false);
  const [writeEvent, setWriteEvent] = useState<any>(null);

  const live = !!cfg?.firm_alerts_live;
  const queue = (events ?? []).filter(e => !e.dispatched_at && !e.dispatch_error);
  const errored = (events ?? []).filter(e => e.dispatch_error && !['seed_backfill', 'pipeline_paused', 'suppressed_by_admin'].includes(e.dispatch_error) && !String(e.dispatch_error).startsWith('HELD'));
  const held = (events ?? []).filter(e => e.dispatch_error && String(e.dispatch_error).startsWith('HELD'));
  const emails24 = (sends ?? []).filter(s2 => s2.channel === 'email' && new Date(s2.sent_at) > new Date(Date.now() - 86400000)).length;
  const deadCounts = useMemo(() => {
    const g: Record<string, number> = {};
    for (const b of deadBells ?? []) g[b.reason] = (g[b.reason] ?? 0) + 1;
    return g;
  }, [deadBells]);

  const loadPreview = async (ev: any) => {
    setPreviewFor(ev.id);
    setPreview(null);
    const { data, error: e } = await supabase.rpc('admin_match_preview', { p_event_id: ev.id });
    setPreview(e ? { error: e.message } : data);
  };

  const runEmit = async (dry: boolean) => {
    setEmitBusy(dry ? 'dry' : 'live');
    try {
      const { data, error: e } = await supabase.rpc('admin_emit_open_events', { p_dry_run: dry });
      if (e) throw new Error(e.message);
      setEmitResult({ dry, data });
      if (!dry) queryClient.invalidateQueries({ queryKey: ['open-events'] });
    } catch (e2) {
      setEmitResult({ dry, error: (e2 as Error).message });
    } finally { setEmitBusy(null); }
  };

  if (isLoading) return <LoadingRow label="Reading the dispatch pipeline" />;
  if (error) return <ErrorPanel error={error} label="firm_open_events" />;

  return (
    <div>
      <PageHeader
        title="Radar ops"
        subtitle="Detection to dispatch to delivery, with a preview of exactly who gets an email before anything is armed."
        actions={
          <Button variant={live ? 'danger' : 'primary'} onClick={() => setConfirmKill(true)}>
            {live ? 'Radar armed, click to pause' : 'Radar paused, click to arm'}
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <StatTile label="Queued" value={queue.length} sub={live ? 'drain sweep runs every minute' : 'suppressed while paused'} accent={queue.length > 0 && live ? 'amber' : 'stone'} />
        <StatTile label="Real errors" value={errored.length} accent={errored.length ? 'rose' : 'stone'} />
        <StatTile label="Held" value={held.length} sub="deliberate holds, release to send" accent="stone" />
        <StatTile label="Alert emails, 24 hours" value={emails24} accent="indigo" />
        <StatTile label="Dead bells" value={(deadBells ?? []).length} sub={`${deadCounts['null_sub_sector'] ?? 0} no sub-sector · ${deadCounts['orphan_firm_name'] ?? 0} orphan firm`} accent={(deadBells ?? []).length ? 'amber' : 'stone'} />
      </div>

      <Section
        title="Open events"
        sub="Newest 60. Preview shows the exact recipient list from the live matcher before you arm or replay anything."
        className="mb-3"
      >
        {(events ?? []).length === 0 ? <EmptyNote>No events yet</EmptyNote> : (
          <TableWrap>
            <thead><tr><Th>Firm</Th><Th>Programme</Th><Th>State</Th><Th>Attempts</Th><Th>When</Th><Th>Actions</Th></tr></thead>
            <tbody>
              {(events ?? []).map(ev => {
                const state = !ev.dispatched_at && !ev.dispatch_error ? 'queued'
                  : ev.dispatch_error === 'seed_backfill' ? 'seed'
                  : ev.dispatch_error === 'pipeline_paused' ? 'paused'
                  : ev.dispatch_error === 'suppressed_by_admin' ? 'suppressed'
                  : String(ev.dispatch_error ?? '').startsWith('HELD') ? 'held'
                  : ev.dispatch_error ? 'error' : 'sent';
                return (
                  <React.Fragment key={ev.id}>
                    <tr>
                      <Td className="font-medium text-stone-900">{ev.firm_name}<span className="text-stone-400 font-normal"> {ev.locale}</span></Td>
                      <Td>{ev.programme_type}{ev.sub_sector ? ` · ${ev.sub_sector}` : ''}</Td>
                      <Td>
                        <Badge tone={state === 'sent' ? 'emerald' : state === 'error' ? 'rose' : state === 'queued' ? 'amber' : state === 'held' ? 'violet' : 'stone'}>
                          {state}
                        </Badge>
                      </Td>
                      <Td className="tabular-nums">{ev.dispatch_attempts ?? 0}</Td>
                      <Td className="whitespace-nowrap">{agoLabel(ev.created_at)}</Td>
                      <Td>
                        <span className="flex flex-wrap gap-1.5">
                          <Button size="sm" onClick={() => loadPreview(ev)}>Preview</Button>
                          <Button size="sm" onClick={() => setRowAction({ kind: 'replay', ev })}>Replay</Button>
                          {state === 'queued' && <Button size="sm" variant="danger" onClick={() => setRowAction({ kind: 'suppress', ev })}>Suppress</Button>}
                        </span>
                      </Td>
                    </tr>
                    {previewFor === ev.id && (
                      <tr><Td colSpan={6}>
                        {!preview ? <LoadingRow label="Running the matcher" /> : preview.error ? <ErrorPanel error={preview.error} /> : (
                          <div className="py-1 text-[12px]">
                            <p className="font-semibold text-stone-800 mb-1">
                              {preview.matches?.length ?? 0} belled recipient{(preview.matches?.length ?? 0) === 1 ? '' : 's'} · {preview.bonus?.length ?? 0} bonus-pick recipients
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {(preview.matches ?? []).map((m: any, i: number) => (
                                <Badge key={i} tone={m.unsubscribed || m.receives_firm_alerts === false ? 'stone' : 'emerald'}>
                                  {m.email ?? m.user_id?.slice(0, 8)} · {m.match_source}{m.unsubscribed ? ' · unsubscribed' : ''}
                                </Badge>
                              ))}
                              {(preview.matches ?? []).length === 0 && <span className="text-stone-400">Nobody matches this event.</span>}
                            </div>
                          </div>
                        )}
                      </Td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </Section>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <Section
          title="Emit from dates"
          sub="The same emitter the 6am cron runs. Dry run shows what would emit; the live run writes real events into the queue."
          actions={
            <>
              <Button size="sm" busy={emitBusy === 'dry'} onClick={() => runEmit(true)}>Dry run</Button>
              <Button size="sm" variant="danger" busy={emitBusy === 'live'} onClick={() => setConfirmEmit(true)}>Emit for real</Button>
            </>
          }
        >
          {!emitResult ? <EmptyNote>Nothing run yet</EmptyNote> : emitResult.error ? <ErrorPanel error={emitResult.error} /> : (
            <pre className="text-[11px] bg-stone-50 rounded-md p-2 overflow-x-auto text-stone-600">{JSON.stringify(emitResult.data, null, 2)}</pre>
          )}
        </Section>

        <Section
          title="Blocked by a stale event"
          sub="Programmes open by date whose alert slot is consumed by an older event, so the cron can never fire for them. Writing a fresh event is the fix."
        >
          {(seedBlocked ?? []).length === 0 ? <EmptyNote>None blocked</EmptyNote> : (
            <TableWrap>
              <thead><tr><Th>Firm</Th><Th>Programme</Th><Th>Opened</Th><Th>Last event</Th><Th></Th></tr></thead>
              <tbody>
                {(seedBlocked ?? []).map(b => (
                  <tr key={b.programme_id}>
                    <Td className="font-medium text-stone-900">{b.firm_name} <span className="text-stone-400 font-normal">{b.locale}</span></Td>
                    <Td>{b.programme_type}{b.sub_sector ? ` · ${b.sub_sector}` : ''}</Td>
                    <Td>{fmtDate(b.open_date)}</Td>
                    <Td>{fmtDate(b.last_event)}</Td>
                    <Td><Button size="sm" onClick={() => setWriteEvent(b)}>Write event</Button></Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <Section
          title="Dead bells"
          sub="Bells that can never fire: no sub-sector (the matcher’s known gap) or pointing at a firm name that no longer exists. The real fix is the matcher; this quantifies it."
        >
          {(deadBells ?? []).length === 0 ? <EmptyNote>Every active bell can fire</EmptyNote> : (
            <>
              <TableWrap>
                <thead><tr><Th>User</Th><Th>Firm</Th><Th>Programme</Th><Th>Reason</Th></tr></thead>
                <tbody>
                  {(deadBells ?? []).slice(0, 25).map((b, i) => (
                    <tr key={i}>
                      <Td><UserLink id={b.user_id} label={b.email} /></Td>
                      <Td>{b.firm_name}</Td>
                      <Td>{b.programme_type}</Td>
                      <Td><Badge tone="amber">{b.reason === 'null_sub_sector' ? 'No sub-sector' : 'Orphan firm name'}</Badge></Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
              {(deadBells ?? []).length > 25 && <p className="text-[11px] text-stone-400 mt-1.5">Showing 25 of {(deadBells ?? []).length}.</p>}
            </>
          )}
        </Section>

        <Section title="Duplicate firm records" sub="The matcher joins on firm name, so twins split bell demand and rows written under the wrong twin alert nobody.">
          {(dupes ?? []).length === 0 ? <EmptyNote>No duplicates</EmptyNote> : (
            <TableWrap>
              <thead><tr><Th>Name</Th><Th>Sector</Th><Th>Tier</Th><Th>Programmes</Th></tr></thead>
              <tbody>
                {(dupes ?? []).map((d, i) => (
                  <tr key={i}>
                    <Td className="font-medium text-stone-900">{d.name}</Td>
                    <Td>{d.sector}</Td>
                    <Td>{d.tier}</Td>
                    <Td className="tabular-nums">{d.programmes}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>
      </div>

      <Section title="Manual send, done safely" sub="The runbook, in order. Every step is a button on this page and every step is audited.">
        <ol className="text-[12.5px] text-stone-600 space-y-1 list-decimal pl-4">
          <li>Verify the application URL in a real browser. Never solve a bot check.</li>
          <li>Write the open event (or fix the programme dates and use the emitter), then Preview it: the exact recipient list, deduped, opt-outs marked.</li>
          <li>Arm the radar. The every-minute drain sweep dispatches the queue.</li>
          <li>Watch deliveries reconcile against the preview under alert sends.</li>
          <li>Pause the radar again, then revalidate the tracker from the Firms and tracker page.</li>
        </ol>
      </Section>

      {/* dialogs */}
      <ConfirmDialog
        open={confirmKill} onClose={() => setConfirmKill(false)}
        title={live ? 'Pause the radar' : 'Arm the radar'}
        tone={live ? 'primary' : 'danger'}
        confirmLabel={live ? 'Pause' : 'Arm'}
        body={live
          ? <>New open events will be stamped suppressed instead of dispatched. Nothing queued is lost.</>
          : <>Real emails start sending within a minute: {queue.length} event{queue.length === 1 ? '' : 's'} currently queued. Preview them first.</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_set_firm_alerts_live', { p_live: !live });
          if (e) throw new Error(e.message);
          queryClient.invalidateQueries();
        }}
      />

      <ConfirmDialog
        open={!!rowAction} onClose={() => setRowAction(null)}
        title={rowAction?.kind === 'replay' ? `Replay ${rowAction?.ev?.firm_name}` : `Suppress ${rowAction?.ev?.firm_name}`}
        tone={rowAction?.kind === 'replay' ? 'danger' : 'primary'}
        confirmLabel={rowAction?.kind === 'replay' ? 'Replay' : 'Suppress'}
        body={rowAction?.kind === 'replay'
          ? <>Clears the dispatch state so the sweep sends this event for real on its next pass. Only do this after previewing the recipients, and only while armed if you mean it.</>
          : <>Marks the event handled without sending anything.</>}
        onConfirm={async () => {
          if (!rowAction) return;
          const fn = rowAction.kind === 'replay' ? 'admin_replay_open_event' : 'admin_suppress_open_event';
          const { error: e } = await supabase.rpc(fn, { p_event_id: rowAction.ev.id });
          if (e) throw new Error(e.message);
          queryClient.invalidateQueries({ queryKey: ['open-events'] });
        }}
      />

      <ConfirmDialog
        open={confirmEmit} onClose={() => setConfirmEmit(false)}
        title="Emit open events for real" tone="danger" confirmLabel="Emit"
        body={<>Writes real events into the queue from current programme dates. {live ? 'The radar is armed, so they dispatch within a minute.' : 'The radar is paused, so they will be stamped suppressed.'} Run the dry run first.</>}
        onConfirm={async () => { await runEmit(false); }}
      />

      <ConfirmDialog
        open={!!writeEvent} onClose={() => setWriteEvent(null)}
        title={`Write an open event for ${writeEvent?.firm_name ?? ''}`}
        tone="danger" confirmLabel="Write event"
        body={<>Inserts a fresh open event for this programme. {live ? 'The radar is armed: it dispatches within a minute.' : 'The radar is paused: it queues suppressed until you replay it.'}</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_insert_open_event', { p_programme_id: writeEvent.programme_id });
          if (e) throw new Error(e.message);
          queryClient.invalidateQueries({ queryKey: ['open-events'] });
          queryClient.invalidateQueries({ queryKey: ['admin_seed_blocked_candidates'] });
        }}
      />
    </div>
  );
};
